import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  clearCredentialMutation,
  type ExtensionResponse,
  errorMessage,
  getExtensionQueryKey,
  listExtensionsQueryKey,
  toggleExtensionMutation,
  updateCredentialMutation,
  updateTeamAccessMutation,
} from "@/api";

/** Configuration changes on one integration; each refreshes its detail and the catalog. */
export function useIntegrationActions(extension: ExtensionResponse) {
  const { t } = useTranslation("adminIntegrations");
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = { ext_uuid: extension.uuid };
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getExtensionQueryKey({ path }) }),
      queryClient.invalidateQueries({ queryKey: listExtensionsQueryKey() }),
    ]);

  const toggle = useMutation(toggleExtensionMutation());
  const save = useMutation(updateCredentialMutation());
  const clear = useMutation(clearCredentialMutation());
  const teams = useMutation(updateTeamAccessMutation());

  return {
    setEnabled: (isEnabled: boolean) =>
      toggle.mutate(
        { path, body: { isEnabled } },
        {
          onSuccess: async () => {
            await refresh();
            toast.success(t(isEnabled ? "edit.turnedOn" : "edit.turnedOff", { name: extension.name }));
          },
          onError: (error) => toast.error(errorMessage(error, t("edit.toggleFailed"))),
        },
      ),
    toggling: toggle.isPending,
    /** Resolves true once saved, so the editor can close. */
    setCredential: async (key: string, label: string, value: string) => {
      try {
        await save.mutateAsync({ path, body: { key, value }, throwOnError: true });
        await refresh();
        toast.success(t("edit.saved", { field: label }));
        return true;
      } catch (error) {
        toast.error(errorMessage(error, t("edit.saveFailed", { field: label })));
        return false;
      }
    },
    clearCredential: (key: string, label: string) =>
      clear.mutate(
        { path: { ...path, credential_key: key } },
        {
          onSuccess: async () => {
            await refresh();
            toast.success(t("edit.cleared", { field: label }));
          },
          onError: (error) => toast.error(errorMessage(error, t("edit.clearFailed", { field: label }))),
        },
      ),
    clearing: clear.isPending ? clear.variables?.path.credential_key : undefined,
    setTeams: (teamUuids: string[]) =>
      teams.mutate(
        { path, body: { teamUuids } },
        {
          onSuccess: async () => {
            await refresh();
            toast.success(t("edit.accessSaved"));
          },
          onError: (error) => toast.error(errorMessage(error, t("edit.accessFailed"))),
        },
      ),
    savingTeams: teams.isPending,
  };
}

export type IntegrationActions = ReturnType<typeof useIntegrationActions>;
